/* eslint-disable no-restricted-syntax */
import { GetStaticProps } from 'next';
import Head from 'next/head';
import { ReactElement } from 'react';

import { LinkButtonRound } from '@components/Buttons/LinkButton';
import { StandardLayout } from '@components/Layouts';
import { Paragraph, StrongText, TextLink } from '@components/Typography';
import { sanityFetch } from '@lib/sanity.fetch';
import { Offence } from 'types/Documents';

export default function Services({ offences }: { offences: Offence[] }) {
  // estimate 65 characters per line and fine middle point to split columns
  const lineCount = offences.map((offence) =>
    Math.ceil((offence.title + offence.summary).length / 65)
  );
  const totalLines = lineCount.reduce((acc, curr) => acc + curr, 0);
  const middle = Math.ceil(totalLines / 2);
  let linesCounted = 0;
  let idx = 0;
  while (linesCounted < middle) {
    linesCounted += lineCount[idx];
    idx++;
  }

  const column1 = offences.slice(0, idx);
  const column2 = offences.slice(idx);

  return (
    <>
      <Head>
        <title key="title">
          Criminal Law Services Hobart | Drug Court, Bail, Appeals | Monk
          Lawyers
        </title>
        <meta
          name="title"
          content="Criminal Law Services Hobart | Drug Court, Bail, Appeals | Monk Lawyers"
          key="metatitle"
        />
        <meta
          name="description"
          content="A list of some of the services provided by our firm, and offences that we can assist with with links to further information"
          key="metadescription"
        />

        <meta
          property="og:title"
          content="Criminal Law Services Hobart | Drug Court, Bail, Appeals | Monk Lawyers"
          key="ogtitle"
        />
        <meta
          property="og:url"
          content="https://www.monklawyers.com.au/services"
          key="ogurl"
        />
        <meta
          property="og:image"
          content={`${process.env.NEXT_PUBLIC_SERVER_URL}/images/Footer logo-12.png`}
          key="ogimage"
        />
        <meta property="og:type" content="website" key="ogtype" />
        <meta
          property="og:description"
          content="Find out about some of the services provided by our firm, and offences that we can assist with with links to further information"
          key="ogdescription"
        />
      </Head>

      <main className="bg-cream text-blue relative container pt-[105px] pb-[350px]">
        <h1>Services</h1>
        <h2>Areas of Criminal Law Practice</h2>
        <Paragraph>
          Defending members of the community charged with any criminal offence
          including -
        </Paragraph>

        <section className="grid grid-cols-1 gap-x-[120px] gap-y-[120px] pt-10 lg:grid-cols-2">
          <ul className="m-0 flex list-none flex-col gap-10 p-0">
            {column1.map((offence) => (
              <li key={offence.slug}>
                <TextLink href={offence.slug}>
                  <StrongText>{offence.title}</StrongText> {offence.summary}
                </TextLink>
              </li>
            ))}
          </ul>
          <ul className="m-0 flex list-none flex-col gap-10 p-0">
            {column2.map((offence) => (
              <li key={offence.slug}>
                <TextLink href={offence.slug}>
                  <StrongText>{offence.title}</StrongText> {offence.summary}
                </TextLink>
              </li>
            ))}
          </ul>
        </section>
        <LinkButtonRound
          href="/contact"
          className="absolute right-[155px] bottom-[155px]"
        >
          contact now
        </LinkButtonRound>
      </main>
    </>
  );
}

Services.getLayout = function getLayout(page: ReactElement) {
  return <StandardLayout>{page}</StandardLayout>;
};

export const getStaticProps: GetStaticProps = async ({
  params,
  draftMode = false,
}) => {
  const offences = await sanityFetch<Offence[]>({
    draftMode,
    query: `//groq
    *[_type == "offence" && !(_id in path("drafts.**"))]
    {
      title,
      summary,
      "slug": slug.current,
      position
    } | order(position asc)`,
  });

  return {
    props: {
      offences,
    },
  };
};
